import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { TaskProgressWebSocketService } from './task-progress-websocket.service';

export interface AnalysisStatus {
  has_active_analysis: boolean;
  task_id?: string;
  project_id?: string;
  status?: string; // pending/in_progress/completed/failed
  started_at?: string;
  progress_percentage?: number; // Progress percentage (0-100)
  /** Seconds: queue wait or running ETA (same as bulk-search TaskStatus) */
  estimated_time_remaining?: number | null;
  current_step?: string | null;
  queue_position?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class AnalysisStatusService implements OnDestroy {
  private readonly baseUrl = environment.apiUrl + '/analysis';
  
  private statusSubject = new BehaviorSubject<AnalysisStatus | null>(null);
  public status$ = this.statusSubject.asObservable();
  
  private pollingInterval?: Subscription;
  /** Subscription to WebSocket analysis messages (when useWebSocket). Do not remove – rollback to polling if needed. */
  private wsSubscription?: Subscription;
  private readonly POLLING_INTERVAL = 8000; // 8 seconds (between 5-10 seconds as per plan)
  private isCancelled = false; // Flag to prevent polling restart after cancellation
  
  constructor(
    private http: HttpClient,
    private taskProgressWs: TaskProgressWebSocketService
  ) {}
  
  ngOnDestroy(): void {
    this.stopStatusPolling();
  }
  
  /**
   * Check if user has active analysis
   */
  checkActiveAnalysis(): Observable<AnalysisStatus> {
    return this.http.get<AnalysisStatus>(`${this.baseUrl}/status`);
  }
  
  /**
   * Get current analysis status (Observable)
   */
  getCurrentAnalysisStatus(): Observable<AnalysisStatus | null> {
    return this.status$;
  }
  
  /**
   * Get current analysis status value
   */
  getCurrentStatus(): AnalysisStatus | null {
    return this.statusSubject.value;
  }
  
  /**
   * Start polling for analysis status (or subscribe to WebSocket when useWebSocket).
   * Polling code kept for rollback – do not remove.
   */
  startStatusPolling(): void {
    if (this.isCancelled) {
      console.log('📊 Polling prevented - analysis was cancelled');
      return;
    }

    if (this.taskProgressWs.useWebSocket) {
      // Live feed: use WebSocket (do not remove – rollback to polling if needed)
      if (this.wsSubscription) return;
      this.taskProgressWs.connect();
      this.fetchStatus(); // initial load via REST
      this.wsSubscription = this.taskProgressWs.analysisMessages$.subscribe((msg) => {
        const raw = msg.data as unknown as Record<string, unknown>;
        if (!raw) return;
        const data = this.normalizeAnalysisStatusFromWs(raw);
        if (data.status === 'cancelled') {
          this.isCancelled = true;
          this.stopStatusPolling();
        }
        this.statusSubject.next(data);
        // Only stop when task is truly done; progress-only payloads may omit has_active_analysis
        const taskDone = data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled';
        if (taskDone || (data.has_active_analysis === false && !data.task_id)) {
          this.stopStatusPolling();
        }
      });
      console.log('📊 Started analysis status via WebSocket');
      return;
    }

    // Fallback: polling (comment out block above and set useWebSocket: false to revert)
    if (this.pollingInterval) return;
    this.fetchStatus();
    this.pollingInterval = interval(this.POLLING_INTERVAL).subscribe(() => {
      this.fetchStatus();
    });
    console.log('📊 Started analysis status polling');
  }
  
  /**
   * Stop polling for analysis status (or WebSocket subscription).
   */
  stopStatusPolling(): void {
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = undefined;
      console.log('📊 Stopped analysis status WebSocket subscription');
    }
    if (this.pollingInterval) {
      this.pollingInterval.unsubscribe();
      this.pollingInterval = undefined;
      console.log('📊 Stopped analysis status polling');
    }
  }
  
  /**
   * Mark analysis as cancelled to prevent polling restart
   */
  markAsCancelled(): void {
    this.isCancelled = true;
    this.stopStatusPolling();
    console.log('🛑 Analysis marked as cancelled - polling disabled');
  }
  
  /**
   * Reset cancelled flag (when new analysis starts)
   */
  resetCancelledFlag(): void {
    this.isCancelled = false;
  }
  
  /**
   * Normalize WebSocket payload to AnalysisStatus.
   * Bulk-search progress updates (path "analysis") send task progress without has_active_analysis or project_id;
   * derive has_active_analysis from status and preserve project_id from current status so the project table keeps showing progress.
   */
  private normalizeAnalysisStatusFromWs(raw: Record<string, unknown>): AnalysisStatus {
    const status = raw['status'] as string | undefined;
    const taskId = raw['task_id'] as string | undefined;
    const current = this.statusSubject.value;
    const isActive =
      status === 'pending' ||
      status === 'queued' ||
      status === 'progress' ||
      status === 'in_progress';
    const hasActive = raw['has_active_analysis'] !== undefined
      ? Boolean(raw['has_active_analysis'])
      : (taskId ? isActive : false);
    const projectId = (raw['project_id'] as string | undefined) ?? (current?.task_id === taskId ? current?.project_id : undefined);
    return {
      has_active_analysis: hasActive,
      task_id: taskId ?? current?.task_id,
      project_id: projectId ?? current?.project_id,
      status: status ?? current?.status,
      started_at: (raw['started_at'] as string | undefined) ?? current?.started_at,
      progress_percentage: (raw['progress_percentage'] as number | undefined) ?? current?.progress_percentage,
      estimated_time_remaining:
        (raw['estimated_time_remaining'] as number | undefined) ?? current?.estimated_time_remaining,
      current_step: (raw['current_step'] as string | undefined) ?? current?.current_step,
      queue_position: (raw['queue_position'] as number | undefined) ?? current?.queue_position,
    };
  }

  /**
   * Fetch status from server
   */
  private fetchStatus(): void {
    this.checkActiveAnalysis().subscribe({
      next: (status) => {
        const previousStatus = this.statusSubject.value;
        
        // Check if status changed from active to completed/failed/cancelled
        if (previousStatus && previousStatus.has_active_analysis && !status.has_active_analysis) {
          // Analysis completed - notification will be handled by component
          console.log('✅ Analysis completed or cancelled');
        }
        
        // Check if status is cancelled
        if (status.status === 'cancelled') {
          console.log('🛑 Analysis was cancelled - stopping polling');
          this.isCancelled = true;
          this.stopStatusPolling();
        }
        
        this.statusSubject.next(status);
        
        // Stop polling if no active analysis or if cancelled
        if (!status.has_active_analysis || status.status === 'cancelled') {
          if (status.status === 'cancelled') {
            this.isCancelled = true;
          }
          this.stopStatusPolling();
        }
      },
      error: (error) => {
        console.error('Error fetching analysis status:', error);
        // Don't update status on error, keep previous value
      }
    });
  }
  
  /**
   * Manually refresh status (does not restart polling)
   */
  refreshStatus(): void {
    // Only fetch status once, don't restart polling
    this.fetchStatus();
  }
}

